import React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowUp, Paperclip, Square, X, StopCircle, Mic } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

// Textarea Component
interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  className?: string;
}
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(({ className, ...props }, ref) => (
  <textarea
    className={cn(
      "flex w-full rounded-md border-none bg-transparent px-3 py-2.5 text-base placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 min-h-[44px] resize-none scrollbar-thin",
      className
    )}
    ref={ref}
    rows={1}
    {...props}
  />
));
Textarea.displayName = "Textarea";

// Tooltip Components
const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// Dialog Components for Image Preview
const Dialog = DialogPrimitive.Root;
const DialogPortal = DialogPrimitive.Portal;
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-[90vw] md:max-w-[800px] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-0 shadow-xl duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-2xl",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 z-10 rounded-full bg-muted/80 p-2 hover:bg-muted transition-all">
        <X className="h-5 w-5 text-muted-foreground hover:text-foreground" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight text-foreground", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

// Button Component
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "default", ...props }, ref) => {
    const variantClasses = {
      default: "bg-primary hover:bg-primary/90 text-primary-foreground",
      outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
      ghost: "bg-transparent hover:bg-accent hover:text-accent-foreground",
    };
    const sizeClasses = {
      default: "h-10 px-4 py-2",
      sm: "h-8 px-3 text-sm",
      lg: "h-12 px-6",
      icon: "h-8 w-8 rounded-full aspect-[1/1]",
    };
    return (
      <button
        className={cn(
          "inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 rounded-md",
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

// ImageViewDialog Component
interface ImageViewDialogProps {
  imageUrl: string | null;
  onClose: () => void;
}
const ImageViewDialog: React.FC<ImageViewDialogProps> = ({ imageUrl, onClose }) => {
  if (!imageUrl) return null;
  return (
    <Dialog open={!!imageUrl} onOpenChange={onClose}>
      <DialogContent className="p-0 border-none bg-transparent shadow-none max-w-[90vw] md:max-w-[800px]">
        <DialogTitle className="sr-only">Image Preview</DialogTitle>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="relative bg-background rounded-2xl overflow-hidden shadow-2xl"
        >
          <img
            src={imageUrl}
            alt="Full preview"
            className="w-full max-h-[80vh] object-contain rounded-2xl"
          />
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

// SmartPromptInput Context and Components
interface SmartPromptInputContextType {
  isLoading: boolean;
  value: string;
  setValue: (value: string) => void;
  maxHeight: number | string;
  onSubmit?: () => void;
  disabled?: boolean;
}
const SmartPromptInputContext = React.createContext<SmartPromptInputContextType>({
  isLoading: false,
  value: "",
  setValue: () => {},
  maxHeight: 240,
  onSubmit: undefined,
  disabled: false,
});

function useSmartPromptInput() {
  const context = React.useContext(SmartPromptInputContext);
  if (!context) throw new Error("useSmartPromptInput must be used within a SmartPromptInput");
  return context;
}

interface SmartPromptInputProps {
  isLoading?: boolean;
  value?: string;
  onValueChange?: (value: string) => void;
  maxHeight?: number | string;
  onSubmit?: () => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

const SmartPromptInput = React.forwardRef<HTMLDivElement, SmartPromptInputProps>(
  (
    {
      className,
      isLoading = false,
      maxHeight = 240,
      value,
      onValueChange,
      onSubmit,
      children,
      disabled = false,
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(value || "");
    const handleChange = (newValue: string) => {
      setInternalValue(newValue);
      onValueChange?.(newValue);
    };
    return (
      <TooltipProvider>
        <SmartPromptInputContext.Provider
          value={{
            isLoading,
            value: value ?? internalValue,
            setValue: onValueChange ?? handleChange,
            maxHeight,
            onSubmit,
            disabled,
          }}
        >
          <div
            ref={ref}
            className={cn(
              "rounded-3xl border bg-background p-2 transition-all duration-300",
              isLoading && "border-destructive/70",
              className
            )}
          >
            {children}
          </div>
        </SmartPromptInputContext.Provider>
      </TooltipProvider>
    );
  }
);
SmartPromptInput.displayName = "SmartPromptInput";

interface SmartPromptInputTextareaProps {
  disableAutosize?: boolean;
  placeholder?: string;
  onFocusChange?: (focused: boolean) => void;
}

const SmartPromptInputTextarea = React.memo(React.forwardRef<HTMLTextAreaElement, SmartPromptInputTextareaProps & React.ComponentProps<typeof Textarea>>(({
  className,
  onKeyDown,
  disableAutosize = false,
  placeholder,
  onFocus,
  onBlur,
  onFocusChange,
  ...props
}, externalRef) => {
  const { setValue, maxHeight, onSubmit, disabled } = useSmartPromptInput();
  const internalRef = React.useRef<HTMLTextAreaElement>(null);
  
  const mergedRef = React.useCallback((element: HTMLTextAreaElement) => {
    internalRef.current = element;
    
    if (typeof externalRef === 'function') {
      externalRef(element);
    } else if (externalRef) {
      externalRef.current = element;
    }
  }, [externalRef]);

  React.useEffect(() => {
    if (disableAutosize || !internalRef.current) return;
    const current = internalRef.current.value;
    internalRef.current.style.height = "auto";
    internalRef.current.style.height =
      typeof maxHeight === "number"
        ? `${Math.min(internalRef.current.scrollHeight, maxHeight)}px`
        : `min(${internalRef.current.scrollHeight}px, ${maxHeight})`;
  }, [maxHeight, disableAutosize]);

  const handleFocus = React.useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    onFocusChange?.(true);
    onFocus?.(e);
  }, [onFocus, onFocusChange]);

  const handleBlur = React.useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    onFocusChange?.(false);
    onBlur?.(e);
  }, [onBlur, onFocusChange]);

  return (
    <Textarea
      ref={mergedRef}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          onSubmit?.();
        }
        onKeyDown?.(e);
      }}
      onChange={(e) => setValue(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={cn("text-base flex-1", className)}
      placeholder={placeholder}
      disabled={disabled}
      {...props}
    />
  );
}));
SmartPromptInputTextarea.displayName = "SmartPromptInputTextarea";

interface SmartPromptInputActionsProps extends React.HTMLAttributes<HTMLDivElement> {}

const SmartPromptInputActions: React.FC<SmartPromptInputActionsProps> = ({ children, className, ...props }) => (
  <div className={cn("flex items-center gap-2", className)} {...props}>
    {children}
  </div>
);

interface SmartPromptInputActionProps extends React.ComponentProps<typeof Tooltip> {
  tooltip: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
}

const SmartPromptInputAction: React.FC<SmartPromptInputActionProps> = ({
  tooltip,
  children,
  className,
  side = "top",
  ...props
}) => {
  const { disabled } = useSmartPromptInput();
  return (
    <Tooltip {...props}>
      <TooltipTrigger asChild disabled={disabled}>
        {children}
      </TooltipTrigger>
      <TooltipContent side={side} className={className}>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

// Main SmartPromptInputBox Component
interface SmartPromptInputBoxProps {
  onSend?: (message: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const SmartPromptInputBox = React.memo(React.forwardRef<HTMLDivElement, SmartPromptInputBoxProps>((props, ref) => {
  const { 
    onSend = () => {}, 
    isLoading = false, 
    placeholder = "Digite sua mensagem...", 
    className,
    disabled = false
  } = props;

  const [input, setInput] = React.useState("");
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const handleSubmit = React.useCallback(() => {
    if (input.trim()) {
      onSend(input.trim());
      setInput("");
      
      // Force clear textarea and reset height
      if (textareaRef.current) {
        textareaRef.current.value = "";
        textareaRef.current.style.height = "auto";
        textareaRef.current.focus();
      }
    }
  }, [input, onSend]);

  // Sync input state with textarea value
  React.useEffect(() => {
    if (textareaRef.current && textareaRef.current.value !== input) {
      textareaRef.current.value = input;
    }
  }, [input]);

  const hasContent = React.useMemo(() => input.trim() !== "", [input]);

  return (
    <>
      <SmartPromptInput
        value={input}
        onValueChange={setInput}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        className={cn(
          "w-full bg-background border-input transition-all duration-300 ease-in-out",
          className
        )}
        disabled={disabled || isLoading}
        ref={ref}
      >
        <SmartPromptInputActions className="flex items-center gap-2 p-0 pt-2">
          <SmartPromptInputTextarea
            ref={textareaRef}
            placeholder={placeholder}
            className="text-base flex-1 !min-h-[32px] !h-8 !py-0 leading-8"
            disableAutosize={false}
          />

          <SmartPromptInputAction
            tooltip={
              isLoading
                ? "Carregando..."
                : hasContent
                ? "Enviar mensagem"
                : "Digite uma mensagem"
            }
          >
            <Button
              variant="default"
              size="icon"
              className={cn(
                "h-8 w-8 rounded-full transition-all duration-200 flex-shrink-0",
                hasContent
                  ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                  : "bg-transparent hover:bg-accent text-muted-foreground hover:text-accent-foreground"
              )}
              onClick={handleSubmit}
              disabled={!hasContent || isLoading}
            >
              {isLoading ? (
                <Square className="h-4 w-4 fill-current animate-pulse" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
            </Button>
          </SmartPromptInputAction>
        </SmartPromptInputActions>
      </SmartPromptInput>

      <ImageViewDialog imageUrl={selectedImage} onClose={() => setSelectedImage(null)} />
    </>
  );
}));
SmartPromptInputBox.displayName = "SmartPromptInputBox";